import SearchIcon from "@mui/icons-material/Search"
import {
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TableContainer,
  TablePagination,
  TextField,
} from "@mui/material"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import { useAppSelector } from "hooks"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import type { IUser } from "types/userTypes"
import { UserListItemMenu } from "./UserListItemMenu"

export function UserList() {
  const navigate = useNavigate()

  const users = useAppSelector((state) => state.users.users)
  const userGroups = useAppSelector((state) => state.usergroup.userGroups)
  const licenses = useAppSelector((state) => state.license.licenses)

  const [searchCategory, setSearchCategory] = useState<string>("Name")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [searchText, setSeachText] = useState<string>("")

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const filterUsers = (value: string, users: Array<IUser>): Array<IUser> => {
    const filteredUsers = users.filter((user) => {
      if (searchCategory === "Name") {
        return (
          user.firstname.toLowerCase().includes(value.toLowerCase()) ||
          user.lastname.toLowerCase().includes(value.toLowerCase())
        )
      } else if (searchCategory === "User Group") {
        const filteredGroups = userGroups.filter((x) =>
          x.name.toLowerCase().includes(value.toLowerCase()),
        )
        const userGroupFound = filteredGroups.find((x) => x.id === user.userGroupId)
        return userGroupFound !== undefined
      } else if (searchCategory === "Email") {
        return user.email.toLowerCase().includes(value.toLowerCase())
      } else if (searchCategory === "PIN") {
        return user.pin.toLowerCase().includes(value.toLowerCase())
      }
      return false
    })

    return filteredUsers
  }

  const getLicenseState = (user: IUser): string => {
    const license = licenses.find((x) => x.assignedUserId === user.id)

    if (license === null || license === undefined) {
      return "No License"
    }

    if (license.licenseCanceled) {
      return `Released on ${new Date(license.cancelationDate).toLocaleDateString()}`
    }

    return "Active"
  }

  const handleSearchChanged = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSeachText(event.target.value)
  }

  return (
    <TableContainer>
      <Grid container paddingTop={1} spacing={2}>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel id="search-group">Search by</InputLabel>
            <Select
              fullWidth
              value={searchCategory}
              labelId="search-group"
              label="Search by"
              onChange={(e) => {
                setSearchCategory(e.target.value)
                setSeachText("")
              }}
            >
              <MenuItem value={"Name"}>Name</MenuItem>
              <MenuItem value={"User Group"}>User Group</MenuItem>
              <MenuItem value={"Email"}>Email</MenuItem>
              <MenuItem value={"PIN"}>PIN</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={9}>
          <TextField
            fullWidth
            id="standard-search"
            label="Search"
            type="text"
            variant="outlined"
            value={searchText}
            onChange={handleSearchChanged}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <div style={{ minHeight: "280px" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>User Group</TableCell>
              <TableCell>PIN</TableCell>
              <TableCell>Dashboard Access</TableCell>
              <TableCell>License</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right" padding="checkbox"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filterUsers(searchText, users)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
                <TableRow key={user.id} hover onClick={() => navigate(user.id)}>
                  <TableCell>{`${user.firstname} ${user.lastname}`}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{userGroups.find((x) => x.id === user.userGroupId)?.name}</TableCell>
                  <TableCell>{user.pin}</TableCell>
                  <TableCell>{user.email ? "Yes" : "No"}</TableCell>
                  <TableCell>{getLicenseState(user)}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <UserListItemMenu user={user} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        component="div"
        rowsPerPageOptions={[5, 10, 25]}
        count={filterUsers(searchText, users).length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  )
}
